/**
 * The Workflow2 redesign canvas as a graph: one trigger on top, its actions in one row underneath, every
 * action linked straight to the trigger (a fan-out) — plus where each block sits (2026-10-10).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Plan: src/pages/Workflow2/PROJECT_NOTES.md (Workflow UI overhaul, step P1). It reads the classic builder's own data
 * model (src/pages/Workflow2/components/WorkflowBuilder.vue): `nodes` = [{ id, type, x, y, properties }],
 * `connections` = [{ id: 'c1', source, target, sourcePoint: 'bottom', targetPoint: 'top' }].
 *
 * What a LOADED workflow looks like (loadNodesFromFlowyData):
 *   • The trigger IS a node (a block with parent -1). Node ids are 1-based numbers.
 *   • One connection per block that has a parent, id `c<n>`, source = the parent block's id. A block whose
 *     parent is null (or points at a block that is not there) gets a connection whose `source` is null
 *     (or a stray id). A block with no parent at all gets no connection.
 *   • Old v1 workflows can have the trigger as a connection TARGET (an action above the trigger).
 * isFanOut() is written against that: only a trigger whose every action has exactly one incoming
 * connection, coming from the trigger, is a fan-out. Anything else (a chain, an orphan, a second trigger)
 * is not, and the new UI hands that workflow to the classic builder instead of silently rewriting it.
 *
 * `isTrigger` is the classic `isTriggerNode(type)`: a function of a node TYPE (not of a node).
 *
 * Pure, and never throws on null / undefined / garbage inputs.
 */

const MARGIN = 16;
const TRIGGER_Y = 48;
const ACTION_GAP_Y = 80;
const BOTTOM_PAD = 48;

function isObject(v) {
  return v !== null && typeof v === 'object';
}

function hasId(n) {
  return isObject(n) && n.id !== null && n.id !== undefined;
}

function usable(nodes) {
  return Array.isArray(nodes) ? nodes.filter(hasId) : [];
}

function triggerTest(isTrigger) {
  return typeof isTrigger === 'function' ? n => !!isTrigger(n.type) : () => false;
}

function positive(v, fallback) {
  return typeof v === 'number' && isFinite(v) && v > 0 ? v : fallback;
}

function notOk(reason) {
  return { ok: false, reason };
}

/** The (first) trigger node, or null. */
export function triggerNode(nodes, isTrigger) {
  const test = triggerTest(isTrigger);
  return usable(nodes).find(test) || null;
}

/** Every non-trigger node, in node order. */
export function orderedActions(nodes, isTrigger) {
  const test = triggerTest(isTrigger);
  return usable(nodes).filter(n => !test(n));
}

/**
 * @returns {{ ok: boolean, reason: string }}
 *   ok     no nodes; a lone trigger; or one trigger whose every action has exactly one incoming
 *          connection, from the trigger, and no other connections exist.
 *   reason '' when ok, else 'no-trigger' | 'multi-trigger' | 'duplicate-id' | 'dangling' | 'inverted' |
 *          'orphan' | 'chain' | 'duplicate'
 */
export function isFanOut(nodes, connections, isTrigger) {
  const list = usable(nodes);
  if (list.length === 0) return { ok: true, reason: '' };

  const test = triggerTest(isTrigger);
  const triggers = list.filter(test);
  if (triggers.length === 0) return notOk('no-trigger');
  if (triggers.length > 1) return notOk('multi-trigger');
  const trigger = triggers[0];

  const byId = {};
  for (let i = 0; i < list.length; i++) {
    const key = String(typeof list[i].id) + ':' + String(list[i].id);
    if (byId[key]) return notOk('duplicate-id');
    byId[key] = list[i];
  }
  const nodeOf = id => (id === null || id === undefined ? null : byId[String(typeof id) + ':' + String(id)] || null);

  const incoming = {};
  const conns = Array.isArray(connections) ? connections : [];
  for (let i = 0; i < conns.length; i++) {
    const c = conns[i];
    if (!isObject(c)) return notOk('dangling');
    const target = nodeOf(c.target);
    if (!target) return notOk('dangling');
    if (target === trigger) return notOk('inverted');
    const source = nodeOf(c.source);
    if (!source) return notOk('orphan');
    if (source !== trigger) return notOk('chain');
    incoming[String(target.id)] = (incoming[String(target.id)] || 0) + 1;
  }

  for (let i = 0; i < list.length; i++) {
    if (list[i] === trigger) continue;
    const n = incoming[String(list[i].id)] || 0;
    if (n === 0) return notOk('orphan');
    if (n > 1) return notOk('duplicate');
  }
  return { ok: true, reason: '' };
}

/**
 * A fresh connections array for the fan-out: trigger → each action, in node order, in exactly the shape
 * the classic createConnection pushes (ids c1..cn). No trigger → [].
 */
export function deriveFanOutConnections(nodes, isTrigger) {
  const trigger = triggerNode(nodes, isTrigger);
  if (!trigger) return [];
  return orderedActions(nodes, isTrigger).map((n, i) => ({
    id: 'c' + (i + 1),
    source: trigger.id,
    target: n.id,
    sourcePoint: 'bottom',
    targetPoint: 'top'
  }));
}

/**
 * Where every block goes (all numbers are whole pixels).
 *
 * The trigger sits at y = 48, centred. The actions sit in one row at y = 48 + triggerHeight + 80, the row
 * centred but never starting left of x = 16 — a row wider than the canvas grows `contentWidth` past
 * `canvasWidth` so the canvas can scroll (the trigger then centres over the row, not over the viewport).
 * The "drag an action here" slot (when `showSlot` and there is a trigger) is the row's last box; with no
 * actions it sits directly under the trigger.
 *
 * @param {Array}    nodes
 * @param {Function} isTrigger classic isTriggerNode(type)
 * @param {Object}   [opts]    canvasWidth, nodeWidth (260), gap (32), triggerHeight (66), actionHeight (100), showSlot
 * @returns {{ positions: Object, slot: ({x:number,y:number}|null), contentWidth: number, contentHeight: number }}
 *   positions  { [nodeId]: { x, y } } for the trigger and every action (a second trigger gets none)
 */
export function layout(nodes, isTrigger, opts) {
  const o = isObject(opts) ? opts : {};
  const canvasWidth = Math.ceil(positive(o.canvasWidth, 0));
  const nodeWidth = positive(o.nodeWidth, 260);
  const gap = typeof o.gap === 'number' && isFinite(o.gap) && o.gap >= 0 ? o.gap : 32;
  const triggerHeight = positive(o.triggerHeight, 66);
  const actionHeight = positive(o.actionHeight, 100);

  const positions = {};
  const trigger = triggerNode(nodes, isTrigger);
  if (!trigger) return { positions, slot: null, contentWidth: canvasWidth, contentHeight: 0 };

  const actions = orderedActions(nodes, isTrigger);
  const showSlot = !!o.showSlot;
  const boxes = actions.length + (showSlot && actions.length ? 1 : 0) || 1;
  const rowWidth = boxes * nodeWidth + (boxes - 1) * gap;
  const startX = Math.round(Math.max(MARGIN, (canvasWidth - rowWidth) / 2));
  const contentWidth = Math.max(canvasWidth, Math.ceil(startX + rowWidth + MARGIN));
  const triggerX = Math.round((contentWidth - nodeWidth) / 2);
  const rowY = Math.round(TRIGGER_Y + triggerHeight + ACTION_GAP_Y);

  positions[trigger.id] = { x: triggerX, y: TRIGGER_Y };
  actions.forEach((n, i) => {
    positions[n.id] = { x: Math.round(startX + i * (nodeWidth + gap)), y: rowY };
  });

  let slot = null;
  if (showSlot) {
    slot = { x: actions.length ? Math.round(startX + actions.length * (nodeWidth + gap)) : triggerX, y: rowY };
  }

  const contentHeight = Math.ceil((actions.length || showSlot ? rowY + actionHeight : TRIGGER_Y + triggerHeight) + BOTTOM_PAD);
  return { positions, slot, contentWidth, contentHeight };
}
